// ============================================================================
// updateNotify.ts — уведомление о доступных обновлениях поставок.
// Политика notify (autoUpdate=false): при старте/фокусе/возврате сети лёгкая
// проверка CheckUpdates; если есть новее и ещё не уведомляли об этой версии —
// постоянный приоритетный тост «Доступны обновления…» (без таймеров-поллинга).
// ============================================================================

import { getSDK } from '@/store/sdkStore';
import { useAuthStore } from '@/store/authStore';
import { useCenterStore } from '@/store/centerStore';
import { useToastStore } from '@/store/toastStore';
import { createLogger } from '@/lib/logger';
import { getDeploymentMode, isContour } from '@/lib/deployment';

const log = createLogger('updateNotify');

let checking = false;

export function initUpdateNotify(): void {
  const run = () => void checkOnce();
  window.addEventListener('focus', run);
  window.addEventListener('online', run);
  // Один отложенный прогон после старта приложения (не поллинг)
  setTimeout(run, 5000);
}

async function checkOnce(): Promise<void> {
  if (checking) return;
  // Закрытый контур: внешний репозиторий обновлений недоступен.
  if (isContour(getDeploymentMode())) return;
  if (!useAuthStore.getState().isAuthenticated) return;
  // ГЕЙТ базы: поставки живут в схеме базы (лаунчер/гонка входа — тишина)
  if (!useCenterStore.getState().baseSlug) return;
  checking = true;
  try {
    const [upd, s] = await Promise.all([
      getSDK().package.checkUpdates({}),
      getSDK().package.getPackageSettings({}),
    ]);
    if (s?.autoUpdate) return; // политика auto — сервер применяет сам по циклу
    const items = (((upd as any)?.items || []) as any[]).filter(
      (u) => u.updateAvailable && u.manifest?.version,
    );
    if (items.length === 0) return;
    const key = 'hub_notified:' + items
      .map((u) => `${u.manifest.templateId || u.manifest.template_id}@${u.manifest.version}`)
      .sort()
      .join(',');
    if (localStorage.getItem(key)) return; // об этой версии уже сообщали
    localStorage.setItem(key, '1');
    const names = items.map((u) => `${u.manifest.name || u.manifest.templateId} v${u.manifest.version}`).join(', ');
    useToastStore.getState().showToast(
      `Доступны обновления поставок: ${names} — раздел «Поставки»`,
      'info',
      { duration: 0, priority: true },
    );
  } catch (e: any) {
    // Нет прав/сети — попытка повторится по focus/online; НЕ молчим: диагностика
    log.debug('checkUpdates skipped', e);
  } finally {
    checking = false;
  }
}
